import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Toggle } from '../../ui';

// 2026-06-08: surfaces the runtime-toggleable `log_content` setting on the usage
// page. Default off means prompt + reply text is dropped before persistence (the
// four telemetry hops on each usage_record keep only routing/structural fields).
// Operators flip this on for local debugging or forensics; the change persists
// in `system_settings` and takes effect on the very next request — no restart.

export interface LogContentToggleProps {
  value: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  onChange: (next: boolean) => Promise<void> | void;
}

export function LogContentToggle({
  value,
  loading,
  saving,
  error,
  onChange,
}: LogContentToggleProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      aria-label="Log full request content"
      data-testid="log-content-toggle"
      className={`mb-6 rounded-2xl border p-5 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.04] to-white/[0.01]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="flex flex-wrap items-start gap-4">
        <span
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
            value
              ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/20'
              : isDark
                ? 'bg-white/[0.05] text-white/60'
                : 'bg-slate-100 text-slate-500'
          }`}
        >
          {value ? (
            <Eye className="h-5 w-5" aria-hidden="true" />
          ) : (
            <EyeOff className="h-5 w-5" aria-hidden="true" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <h2
            className={`text-sm font-semibold ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Log full prompt + reply content
          </h2>
          <p
            className={`mt-1 text-xs ${
              isDark ? 'text-white/60' : 'text-slate-500'
            }`}
          >
            {value ? (
              <>
                <span
                  className={`font-semibold ${
                    isDark ? 'text-amber-300' : 'text-amber-600'
                  }`}
                >
                  Capturing full content
                </span>{' '}
                — incoming prompts and assistant replies are persisted on each new
                usage record. Safe for local debugging only.
              </>
            ) : (
              <>
                <span
                  className={`font-semibold ${
                    isDark ? 'text-emerald-300' : 'text-emerald-600'
                  }`}
                >
                  Content stripped
                </span>{' '}
                — prompts and replies are dropped before persistence. Routing,
                timing, token counts and status are still captured.
              </>
            )}
          </p>

          {error && (
            <p
              data-testid="log-content-toggle-error"
              className={`mt-2 inline-flex items-center gap-1.5 text-xs ${
                isDark ? 'text-rose-300' : 'text-rose-600'
              }`}
            >
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Toggle
            checked={value}
            onChange={(next) => void onChange(next)}
            disabled={loading || saving}
            label={value ? 'Disable content logging' : 'Enable content logging'}
          />
        </div>
      </div>
    </section>
  );
}
